import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'E-mailadres is verplicht';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Voer een geldig e-mailadres in';
    }

    if (!formData?.password) {
      newErrors.password = 'Wachtwoord is verplicht';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Wachtwoord moet minimaal 6 tekens bevatten';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    const { error } = await signIn(formData?.email, formData?.password);
    
    if (error) {
      setErrors({
        general: error?.message || 'Inloggen mislukt. Controleer uw gegevens.'
      });
      setIsLoading(false);
    } else {
      navigate('/homepage');
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card rounded-lg shadow-[0_12px_32px_-8px_rgba(0,0,0,0.15)] hover:shadow-[0_16px_40px_-10px_rgba(0,0,0,0.2)] p-6 border border-border transition-all duration-300 transform perspective-1000" style={{ transformStyle: 'preserve-3d' }}>
        <div className="text-center mb-6">
          <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
            Welkom Terug
          </h1>
          <p className="text-sm font-body text-muted-foreground">
            Log in op uw Verduurzaamwijzer account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors?.general && (
            <div className="bg-error/10 border border-error/20 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                <p className="text-sm font-body text-error">
                  {errors?.general}
                </p>
              </div>
            </div>
          )}

          <Input
            label="E-mailadres"
            type="email"
            name="email"
            placeholder="uw.email@voorbeeld.nl"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <div className="relative">
            <Input
              label="Wachtwoord"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Voer uw wachtwoord in"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-8 p-1 text-muted-foreground hover:text-foreground transition-micro"
              aria-label={showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label="Onthoud mij"
              name="rememberMe"
              checked={formData?.rememberMe}
              onChange={handleInputChange}
            />
            <Link
              to="/forgot-password"
              className="text-sm font-body text-primary hover:text-primary/80 transition-micro"
            >
              Wachtwoord vergeten?
            </Link>
          </div>

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isLoading}
            iconName="LogIn"
            iconPosition="left"
          >
            Inloggen
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm font-body text-muted-foreground">
            Nog geen account?{' '}
            <Link
              to="/customer-registration"
              className="text-primary hover:text-primary/80 font-medium transition-micro"
            >
              Registreer nu
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;